import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import { useSettings } from "@/contexts/SettingsContext";
import { LayoutDashboard, Utensils, CalendarCheck, MessagesSquare, Star, Newspaper, Image as ImageIcon, Settings as Cog, LogOut, PartyPopper } from "lucide-react";

const NAV = [
  { to: "/admin", end: true, key: "dashboard", icon: LayoutDashboard },
  { to: "/admin/menu", key: "menu", icon: Utensils },
  { to: "/admin/reservations", key: "reservations", icon: CalendarCheck },
  { to: "/admin/events", key: "events", icon: PartyPopper },
  { to: "/admin/contact", key: "contact", icon: MessagesSquare },
  { to: "/admin/reviews", key: "reviews", icon: Star },
  { to: "/admin/blog", key: "blog", icon: Newspaper },
  { to: "/admin/gallery", key: "gallery", icon: ImageIcon },
  { to: "/admin/settings", key: "settings", icon: Cog },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#D4AF37]">…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#050505] text-[#F8F5EF] flex" data-testid="admin-layout">
      <aside className="w-64 bg-[#0A0A0A] border-r border-[#D4AF37]/15 flex flex-col">
        <Link to="/admin" className="p-6 border-b border-[#D4AF37]/15">
          <div className="font-serif-luxe text-2xl text-[#D4AF37]">Le 17</div>
          <div className="overline mt-1">Admin</div>
        </Link>
        <nav className="flex-1 py-4">
          {NAV.map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.end}
                data-testid={`admin-nav-${item.key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-sm transition-colors border-l-2 ${isActive ? "border-[#D4AF37] bg-[#D4AF37]/5 text-[#D4AF37]" : "border-transparent text-[#F8F5EF]/70 hover:text-[#D4AF37]"}`
                }
              >
                <Icon size={16} />
                <span>{t(`admin.${item.key}`)}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-[#D4AF37]/15 space-y-2">
          <div className="text-xs text-[#F8F5EF]/50 truncate">{user.email}</div>
          <button data-testid="admin-logout" onClick={async () => { await logout(); nav("/admin/login"); }} className="w-full btn-ghost-gold justify-center text-[10px]">
            <LogOut size={12} /> {t("admin.logout")}
          </button>
          <Link to="/" className="block text-center text-[10px] tracking-[0.2em] uppercase text-[#F8F5EF]/40 hover:text-[#D4AF37] pt-2">← Site public</Link>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
